import React, { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './map.css'
import { EditMode, LatLng, Opening, midpoints } from './mapEdit'
import { TileSource, clampZoom } from './mapTiles'
import { addBaseLayers } from './baseLayers'

// The Leaflet surface of the Map view (handoff §7g, "Drawing gestures"). It
// holds no state of its own: the page passes the boundaries and the edit in
// progress, and every gesture comes back as a call (mapEdit.ts does the
// arithmetic).
//
// - Left-click places a point while drawing; right-click drag pans, so the
//   left button only ever draws. Outside an edit a left drag pans as usual.
// - Drag a vertex to move it; drag a green edge midpoint to add a point
//   there; double-click a vertex to delete it. Double-click zoom is off; the
//   wheel and the zoom control still zoom.
// - The cursor carries the mode: crosshair while drawing, a closed hand while
//   panning.
// - Each outline is a dark casing under a bright stroke, with a light fill.

export interface MapShape {
  id: string
  nodeId: string
  label: string
  points: LatLng[]
  // A shape not yet saved (the import preview), drawn in the edit colour.
  tone?: 'new'
}

export interface MapCanvasProps {
  tiles: TileSource
  // Read once, when the map opens.
  opening: Opening
  shapes: MapShape[]
  selected: string | null
  edit: { nodeId: string; mode: EditMode; points: LatLng[] } | null
  // A new key fits the view to these points.
  zoomTo: { key: number; points: LatLng[] } | null
  onSelect: (nodeId: string) => void
  onAdd: (p: LatLng) => void
  onMove: (i: number, p: LatLng) => void
  onInsert: (after: number, p: LatLng) => void
  onDelete: (i: number) => void
}

const ll = (p: LatLng): L.LatLngTuple => [p.lat, p.lng]
const pt = (q: L.LatLng): LatLng => ({ lat: q.lat, lng: q.lng })
const vertexIcon = L.divIcon({ className: 'dp-map-vertex', iconSize: [14, 14] })
const midIcon = L.divIcon({ className: 'dp-map-mid', iconSize: [11, 11] })

type Line = L.Polyline<any, any>

function outline(layer: L.LayerGroup, points: LatLng[], strokeClass: string): { casing: Line; stroke: Line } {
  const at = points.map(ll)
  const closed = points.length >= 3
  const casing: Line = closed ? L.polygon(at, { className: 'dp-map-casing', interactive: false, fill: false }) : L.polyline(at, { className: 'dp-map-casing', interactive: false })
  const stroke: Line = closed ? L.polygon(at, { className: strokeClass }) : L.polyline(at, { className: strokeClass, interactive: false })
  casing.addTo(layer)
  stroke.addTo(layer)
  return { casing, stroke }
}

export function MapCanvas(props: MapCanvasProps) {
  const box = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const shapesRef = useRef<L.LayerGroup | null>(null)
  const editRef = useRef<L.LayerGroup | null>(null)
  const cb = useRef(props)
  cb.current = props
  const { tiles, shapes, selected, edit, zoomTo } = props

  useEffect(() => {
    const el = box.current!
    const map = L.map(el, { doubleClickZoom: false, maxZoom: tiles.maxZoom })
    const unbase = addBaseLayers(map, tiles)
    const o = cb.current.opening
    if (o.kind === 'centre') map.setView(ll(o.centre), clampZoom(o.zoom, tiles))
    else if (o.kind === 'fit') map.fitBounds(L.latLngBounds(o.points.map(ll)), { padding: [32, 32], maxZoom: tiles.maxZoom })
    else map.setView([0, 0], 1)
    shapesRef.current = L.layerGroup().addTo(map)
    editRef.current = L.layerGroup().addTo(map)

    map.on('click', (e: L.LeafletMouseEvent) => {
      if (cb.current.edit?.mode === 'draw') cb.current.onAdd(pt(e.latlng))
    })

    // Right-button pan. Leaflet's own drag only takes the left button.
    let from: { x: number; y: number } | null = null
    const down = (ev: MouseEvent) => {
      if (ev.button !== 2) return
      from = { x: ev.clientX, y: ev.clientY }
      el.classList.add('dp-map-panning')
      ev.preventDefault()
    }
    const move = (ev: MouseEvent) => {
      if (!from) return
      map.panBy([from.x - ev.clientX, from.y - ev.clientY], { animate: false })
      from = { x: ev.clientX, y: ev.clientY }
    }
    const up = () => {
      if (!from) return
      from = null
      el.classList.remove('dp-map-panning')
    }
    const menu = (ev: Event) => ev.preventDefault()
    el.addEventListener('mousedown', down)
    el.addEventListener('contextmenu', menu)
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
    mapRef.current = map
    const size = setTimeout(() => map.invalidateSize(), 0)
    return () => {
      clearTimeout(size)
      el.removeEventListener('mousedown', down)
      el.removeEventListener('contextmenu', menu)
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      unbase()
      map.remove()
      mapRef.current = null
    }
  }, [tiles.url, tiles.attribution, tiles.maxZoom])

  // The drawn boundaries; the one being edited is drawn by the editor.
  const editing = edit?.nodeId || null
  useEffect(() => {
    const layer = shapesRef.current
    if (!layer) return
    layer.clearLayers()
    for (const s of shapes) {
      if (s.nodeId === editing) continue
      const { stroke } = outline(layer, s.points, s.nodeId === selected ? 'dp-map-stroke dp-map-on' : s.tone === 'new' ? 'dp-map-stroke dp-map-new' : 'dp-map-stroke')
      stroke.bindTooltip(s.label, { permanent: true, direction: 'center', className: 'dp-map-label' })
      stroke.on('click', (e) => {
        L.DomEvent.stopPropagation(e)
        if (!cb.current.edit) cb.current.onSelect(s.nodeId)
      })
    }
  }, [shapes, selected, editing])

  useEffect(() => {
    const map = mapRef.current
    const layer = editRef.current
    if (!map || !layer) return
    layer.clearLayers()
    const el = box.current!
    el.classList.toggle('dp-map-drawing', edit?.mode === 'draw')
    if (!edit) {
      map.dragging.enable()
      return
    }
    map.dragging.disable()
    const { casing, stroke } = outline(layer, edit.points, 'dp-map-stroke dp-map-editing')
    // Leaflet only treats a marker as a click's target if it listens for
    // clicks; without this, clicking a point (or double-clicking it to delete
    // it) falls through to the map and places new points on top of it.
    const own = (m: L.Marker) => m.on('click', (e) => L.DomEvent.stopPropagation(e))
    edit.points.forEach((p, i) => {
      const m = L.marker(ll(p), { draggable: true, icon: vertexIcon, keyboard: false, title: `Point ${i + 1}` })
      m.on('drag', () => {
        const now = edit.points.map(ll)
        const q = m.getLatLng()
        now[i] = [q.lat, q.lng]
        casing.setLatLngs(now)
        stroke.setLatLngs(now)
      })
      m.on('dragend', () => cb.current.onMove(i, pt(m.getLatLng())))
      m.on('dblclick', (e) => {
        L.DomEvent.stopPropagation(e)
        cb.current.onDelete(i)
      })
      own(m).addTo(layer)
    })
    for (const mid of midpoints(edit.points)) {
      const m = L.marker(ll(mid.at), { draggable: true, icon: midIcon, keyboard: false, title: 'Drag to add a point' })
      m.on('dragend', () => cb.current.onInsert(mid.after, pt(m.getLatLng())))
      own(m).addTo(layer)
    }
  }, [edit])

  const key = zoomTo?.key
  useEffect(() => {
    const map = mapRef.current
    if (!map || !zoomTo || !zoomTo.points.length) return
    map.fitBounds(L.latLngBounds(zoomTo.points.map(ll)), { padding: [40, 40], maxZoom: tiles.maxZoom })
  }, [key])

  return <div ref={box} className="dp-map" data-testid="site-map" />
}
